import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import { Article, ArticleFilters } from '../types/api.types';

// Export ArticleFilters type for backward compatibility
export type { ArticleFilters } from '../types/api.types';

export interface SortOptions {
  sort_by: string;
  sort_direction: 'asc' | 'desc';
}

export interface FilteredArticlesParams {
  databaseId: number | string | null;
  filters?: ArticleFilters;
  search?: string;
  sortOptions?: SortOptions;
  page?: number;
  pageSize?: number;
}

export interface FilteredArticlesResponse {
  articles: Article[];
  count?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  next?: boolean;
  previous?: boolean;
}

export function useFilteredArticles(
  {
    databaseId,
    filters = {},
    search = '',
    sortOptions = { sort_by: 'id', sort_direction: 'asc' },
    page,
    pageSize = 50,
  }: FilteredArticlesParams,
  options?: UseQueryOptions<FilteredArticlesResponse, Error> & { keepPreviousData?: boolean }
) {
  return useQuery<FilteredArticlesResponse, Error>({
    queryKey: ['filteredArticles', databaseId, filters, search, sortOptions, page, pageSize],
    queryFn: async () => {
      if (!databaseId) throw new Error('Database ID is required');
      // Send filters in new format (array) or old format (object) based on what we receive
      const requestBody: any = {
        filters: Array.isArray(filters) ? filters : filters || {},
        search,
        sort_by: sortOptions.sort_by,
        sort_direction: sortOptions.sort_direction,
      };
      
      // Add pagination params if page is specified
      if (page !== undefined) {
        requestBody.page = page;
        requestBody.page_size = pageSize;
      }
      
      const response = await api.post(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.FILTERED_ARTICLES(databaseId)}`,
        requestBody
      );
      
      const articles = (response.data.articles || []).map((item: any) => ({
        id: item.ref,
        name: item.nom,
        ...item,
      }));
      
      // If pagination is used, return paginated response
      if (page !== undefined && response.data.count !== undefined) {
        return {
          articles,
          count: response.data.count,
          page: response.data.page,
          pageSize: response.data.page_size,
          totalPages: response.data.total_pages,
          next: response.data.next || false,
          previous: response.data.previous || false,
        } as FilteredArticlesResponse;
      }
      
      // Backward compatibility: return simple response
      return { articles } as FilteredArticlesResponse;
    },
    enabled: !!databaseId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    keepPreviousData: true,
    ...options,
  });
} 